import { useState, type ReactNode } from "react";
import { Alert, Button, Drawer } from "@/deps/ui-library";

/** The frame every change shares: what changes, when, what it charges, and Confirm. */
export function ChangeDrawer({
  title,
  description,
  rows,
  children,
  confirm,
  onClose,
  dismissLabel = "Cancel",
}: {
  title: string;
  description: string;
  rows: Array<{ label: string; value: string }>;
  children?: ReactNode;
  confirm: {
    label: string;
    danger?: boolean;
    disabled?: boolean;
    onConfirm: () => Promise<unknown>;
  };
  onClose: () => void;
  /** The footer's dismiss button, when "Cancel" would read as the change itself. */
  dismissLabel?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const run = async () => {
    setBusy(true);
    setRefused(null);
    try {
      await confirm.onConfirm();
      onClose();
    } catch (failure) {
      setRefused(failure instanceof Error ? failure.message : "That couldn't be saved.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Drawer.Default
      title={title}
      description={description}
      rootProps={{
        open: true,
        onOpenChange: (details: { open: boolean }) => !details.open && onClose(),
      }}
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button colorScheme="default" variant="outline" size="sm" onClick={onClose}>
            {dismissLabel}
          </Button>
          <Button
            colorScheme={confirm.danger ? "danger" : "primary"}
            size="sm"
            loading={busy}
            disabled={confirm.disabled}
            onClick={() => void run()}
          >
            {confirm.label}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {children}
        {rows.length ? (
          <dl className="m-0 flex flex-col gap-2 text-sm">
            {rows.map((row) => (
              <div key={row.label} className="flex justify-between gap-4">
                <dt className="text-fg-muted">{row.label}</dt>
                <dd className="text-fg m-0 text-right">{row.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {refused ? (
          <Alert config={{ variant: "subtle", colorScheme: "danger" }} description={refused} />
        ) : null}
      </div>
    </Drawer.Default>
  );
}
